import { useCallback, useEffect, useRef, useState } from 'react';

export interface VoiceRecordingResult {
  blob: Blob;
  file: File;
  durationSeconds: number;
}

export function useVoiceRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [waveformLevels, setWaveformLevels] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const durationTimerRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const selectedMimeTypeRef = useRef<string>('audio/webm');

  // Clean up all resources
  const cleanup = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch {
        // ignore
      }
      audioContextRef.current = null;
    }
    mediaRecorderRef.current = null;
    analyserRef.current = null;
    setIsRecording(false);
    setIsPaused(false);
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    setError(null);
    audioChunksRef.current = [];
    setWaveformLevels([]);
    setRecordingDuration(0);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Audio recording is not supported on this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioStreamRef.current = stream;

      // Determine supported mime type
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
      ];
      let chosenMime = '';
      for (const mime of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mime)) {
          chosenMime = mime;
          break;
        }
      }
      selectedMimeTypeRef.current = chosenMime || 'audio/webm';

      const recorder = chosenMime ? new MediaRecorder(stream, { mimeType: chosenMime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      // Set up Web Audio Analyser for live visualizer
      try {
        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new AudioCtx();
        audioContextRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 64;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const updateWaveform = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);

          // Calculate average amplitude
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avg = sum / dataArray.length;
          // Normalize to 0..1 scale with a minimum floor for visual feedback
          const normalized = Math.max(0.08, Math.min(1, avg / 128));

          setWaveformLevels((prev) => {
            const next = [...prev, normalized];
            // Keep latest 28 bars for visualizer width
            return next.length > 28 ? next.slice(next.length - 28) : next;
          });

          animFrameRef.current = requestAnimationFrame(updateWaveform);
        };
        animFrameRef.current = requestAnimationFrame(updateWaveform);
      } catch (audioCtxErr) {
        console.warn('AudioContext visualization setup failed, continuing without visualizer:', audioCtxErr);
      }

      startTimeRef.current = Date.now();
      recorder.start(100); // chunk every 100ms
      setIsRecording(true);
      setIsPaused(false);

      durationTimerRef.current = window.setInterval(() => {
        const elapsed = Math.round((Date.now() - startTimeRef.current) / 1000);
        setRecordingDuration(elapsed);
      }, 500);
    } catch (err: unknown) {
      cleanup();
      const message = err instanceof Error ? err.message : 'Could not access microphone';
      if (message.includes('Permission') || message.includes('denied')) {
        setError('Microphone permission denied. Please allow microphone access in your browser settings.');
      } else {
        setError(message);
      }
    }
  }, [cleanup]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    audioChunksRef.current = [];
    cleanup();
    setWaveformLevels([]);
    setRecordingDuration(0);
  }, [cleanup]);

  const stopRecording = useCallback((): Promise<VoiceRecordingResult | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        cleanup();
        resolve(null);
        return;
      }

      const finalDuration = Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000));

      recorder.onstop = () => {
        const rawMime = selectedMimeTypeRef.current || 'audio/webm';
        const baseMime = rawMime.split(';')[0].trim();
        const blob = new Blob(audioChunksRef.current, { type: baseMime });

        let ext = 'webm';
        if (baseMime.includes('mp4') || baseMime.includes('m4a')) ext = 'm4a';
        else if (baseMime.includes('ogg')) ext = 'ogg';
        else if (baseMime.includes('wav')) ext = 'wav';

        const fileName = `voice-message-${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: baseMime });

        cleanup();
        resolve({ blob, file, durationSeconds: finalDuration });
      };

      try {
        recorder.stop();
      } catch (err) {
        console.error('Failed to stop MediaRecorder:', err);
        cleanup();
        resolve(null);
      }
    });
  }, [cleanup]);

  return {
    isRecording,
    isPaused,
    recordingDuration,
    waveformLevels,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
